import { useCallback, useEffect, useState } from 'react';
import type { Idioma } from '@matias-asset-os/i18n';
import { Visor, type Archivo } from './Visor.js';

/**
 * Los ficheros de la aplicacion se abren DENTRO de la aplicacion.
 *
 * En vez de cambiar los veinte sitios que enlazan una foto, una factura o un
 * informe —y acordarse de hacerlo en el veintiuno—, se escucha el clic una vez
 * aqui: si el enlace apunta a un fichero de nuestra API, se abre en el visor.
 *
 * Se respeta lo que el usuario pide a proposito: abrir en otra pestana con el
 * boton central o con Ctrl/Cmd, y descargar, siguen funcionando como siempre.
 */

/** Lo que sirve la API como fichero: adjuntos, informes y facturas. */
function esFichero(ruta: string): boolean {
  return (
    ruta.startsWith('/api/adjuntos/') ||
    ruta.startsWith('/api/informes/') ||
    ruta.startsWith('/api/facturas/') ||
    ruta.startsWith('/api/incidencias/')
  );
}

export function CapturarArchivos({ idioma }: { idioma: Idioma }) {
  const [archivo, setArchivo] = useState<Archivo | null>(null);

  useEffect(() => {
    const alPulsar = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const destino = e.target as HTMLElement | null;
      const enlace = destino?.closest?.('a');
      // El propio visor lleva un «Abrir aparte»: ese si sale fuera.
      if (!enlace || enlace.hasAttribute('download') || enlace.closest('.visor')) return;
      const href = enlace.getAttribute('href') ?? '';
      if (!href) return;
      let ruta: string;
      try {
        const url = new URL(href, location.href);
        if (url.origin !== location.origin) return;
        ruta = url.pathname;
      } catch {
        return;
      }
      if (!esFichero(ruta)) return;
      e.preventDefault();
      // Una entrada de historia por archivo abierto: el gesto de atras del
      // movil cierra el visor y devuelve a donde se estaba.
      history.pushState({ ...(history.state ?? {}), visor: true }, '');
      setArchivo({
        url: href,
        titulo: enlace.dataset['titulo'] ?? enlace.textContent?.trim() ?? '',
      });
    };
    document.addEventListener('click', alPulsar);
    return () => document.removeEventListener('click', alPulsar);
  }, []);

  const cerrar = useCallback(() => setArchivo(null), []);

  /*
    Cerrar desde el boton es lo mismo que volver atras: se deshace la entrada
    que se apilo al abrir, y asi el historial no se llena de visores cerrados.
  */

  if (!archivo) return null;
  return <Visor idioma={idioma} archivo={archivo} onCerrar={cerrar} />;
}
